import React from 'react';
import ClickableElement from './ClickableElement';
import { FightPrediction } from '../types';
import { analytics } from '../utils/analytics';

interface FightCardProps {
  fight: FightPrediction;
  isSelected: boolean;
  onClick: () => void;
}

const FightCard: React.FC<FightCardProps> = ({ fight, isSelected, onClick }) => {
  const isMobile = window.innerWidth < 768;
  const fighter1Win = fight.fighter1.winProbability > fight.fighter2.winProbability;
  const fighter2Win = fight.fighter2.winProbability > fight.fighter1.winProbability;
  
  // Round the win probabilities to the nearest integer for display
  const fighter1Percentage = Math.round(fight.fighter1.winProbability);
  const fighter2Percentage = Math.round(fight.fighter2.winProbability);
  
  const handleClick = () => {
    // Track the click event
    analytics.trackFighterCardClick(
      `${fight.fighter1.name} vs ${fight.fighter2.name}`,
      'prediction'
    );
    onClick();
  };
  
  return (
    <ClickableElement
      as="button"
      className={`w-full flex items-center justify-between py-4 rounded-xl text-left ${
        isMobile
          ? 'px-4 bg-background-secondary dark:bg-dark-background-secondary'
          : isSelected
            ? 'px-4 bg-zinc-100 dark:bg-zinc-800'
            : 'px-4 hover:bg-background-secondary dark:hover:bg-dark-background-secondary'
      }`}
      onClick={handleClick}
      aria-label={`Fight between ${fight.fighter1.name} and ${fight.fighter2.name}`}
    >
      {/* Fighter 1 vertical line */}
      <div className="flex h-10 items-center">
        <div 
          className={`w-0.5 h-8 rounded-full ${
            fighter1Win 
            ? 'bg-fighter-win dark:bg-dark-fighter-win' 
            : !isMobile && isSelected
              ? 'bg-zinc-400 dark:bg-zinc-600'
              : 'bg-zinc-200 dark:bg-zinc-700'
          }`}
        ></div>
      </div>
      
      <div className="flex flex-col ml-3 flex-1 min-w-0 max-w-[40%]">
        <span className={`text-base font-semibold truncate ${
          fighter1Win || (!isMobile && isSelected)
            ? 'text-text-primary dark:text-dark-text-primary' 
            : 'text-text-tertiary dark:text-dark-text-tertiary'
        }`}>
          {fight.fighter1.name}
        </span>
        <span className={`text-xs font-semibold ${
          fighter1Win 
            ? 'text-fighter-win dark:text-dark-fighter-win' 
            : 'text-text-tertiary dark:text-dark-text-tertiary'
        }`}>
          {fighter1Percentage}%
        </span>
      </div>
      
      <div className="flex items-center text-text-tertiary dark:text-dark-text-tertiary px-3 md:px-5 mx-0 md:mx-1 min-w-[32px] justify-center">
        <span className="text-sm">vs</span>
      </div>
      
      <div className="flex flex-col items-end mr-3 flex-1 min-w-0 max-w-[40%]">
        <span className={`text-base font-semibold truncate text-right w-full ${
          fighter2Win || (!isMobile && isSelected)
            ? 'text-text-primary dark:text-dark-text-primary' 
            : 'text-text-tertiary dark:text-dark-text-tertiary'
        }`}>
          {fight.fighter2.name}
        </span>
        <span className={`text-xs font-semibold text-right w-full ${
          fighter2Win 
            ? 'text-fighter-win dark:text-dark-fighter-win' 
            : 'text-text-tertiary dark:text-dark-text-tertiary'
        }`}>
          {fighter2Percentage}%
        </span>
      </div>
      
      {/* Fighter 2 vertical line */}
      <div className="flex h-10 items-center">
        <div 
          className={`w-0.5 h-8 rounded-full ${
            fighter2Win 
            ? 'bg-fighter-win dark:bg-dark-fighter-win' 
            : !isMobile && isSelected
              ? 'bg-zinc-400 dark:bg-zinc-600'
              : 'bg-zinc-200 dark:bg-zinc-700'
          }`}
        ></div>
      </div>
    </ClickableElement>
  );
};

export default FightCard; 